import {ApiResponse, PagedListResult} from '@/core/models/api-response.model';

/**
 * Readers for PMCC API responses. A body may be the `ApiResponse` envelope (`{correlationId, data, message}`)
 * or the bare payload, and a list may be `data: T[]` with the paging fields on the envelope (or its `pagination`)
 * or `data: {items, totalCount}`; these accept either so the services don't depend on which one a backend sends.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isEnvelope(value: unknown): value is ApiResponse<unknown> {
    return isRecord(value) && 'data' in value && ('correlationId' in value || 'message' in value);
}

/** The payload of a response: `data` when it is an envelope, else the body itself (null for an empty body). */
export function unwrapData<T>(body: unknown): T | null {
    const data = isEnvelope(body) ? body.data : body;
    return (data ?? null) as T | null;
}

/** The `message` of an envelope response, if any. */
export function envelopeMessage(body: unknown): string | null {
    return isEnvelope(body) && typeof body.message === 'string' && body.message.trim() ? body.message.trim() : null;
}

/**
 * An API timestamp as ISO UTC. The PMCC API sends UTC without a zone and with microseconds
 * ("2026-10-14 05:18:50.766617"), which browsers read as local time or reject; values with a zone pass through.
 */
export function apiUtcDate<T extends string | null | undefined>(value: T): T | string {
    if (!value) return value;
    const match = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)(?:\.(\d+))?$/.exec(value.trim());
    if (!match) return value;
    const [, date, time, fraction] = match;
    return `${date}T${time}${fraction ? '.' + fraction.slice(0, 3).padEnd(3, '0') : ''}Z`;
}

const firstNumber =(...values: unknown[]) => values.find((v): v is number => typeof v === 'number');

/** A page of a list response; `pageNumber` / `pageSize` default to the request's when the API omits them. */
export function toPagedResult<T>(body: unknown, pageNumber: number, pageSize: number): PagedListResult<T> {
    const env = isEnvelope(body) ? body : null;
    const data = env ? env.data : body;
    const inner = isRecord(data) ? data : null;
    const items = (Array.isArray(data) ? data : Array.isArray(inner?.['items']) ? inner['items'] : []) as T[];
    const paging = [inner, env, env?.pagination ?? null].filter((s): s is Record<string, unknown> => isRecord(s));
    const field = (...names: string[]) => firstNumber(...paging.flatMap((s) => names.map((n) => s[n])));
    return {
        items,
        totalCount: field('totalCount', 'totalItems', 'totalRecords') ?? items.length,
        pageNumber: field('pageNumber', 'currentPage') ?? pageNumber,
        pageSize: field('pageSize', 'perPage') ?? pageSize,
    };
}
